import React, { useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  RegistrationProvider,
  useRegistration,
} from "@/modules/eventRegistration/features/EventRegistration/context/RegistrationContext";
import { useCheckoutLayout } from "@/components/CheckoutLayout/CheckoutLayout";
import { StepIndicator } from "@/modules/eventRegistration/features/EventRegistration/components/StepIndicator/StepIndicator";
import { ReviewStep } from "@/modules/eventRegistration/features/EventRegistration/components/ReviewStep/ReviewStep";
import EventCheckoutShellAdapter from "@/modules/eventRegistration/features/EventRegistration/components/EventCheckoutShellAdapter/EventCheckoutShellAdapter";
import { formatDateRangeInTimeZone } from "@/modules/eventRegistration/features/EventDirectory/utils/dateUtils";

const STEPS = [
  { label: "Registration" },
  { label: "Payment" },
  { label: "Confirmation" },
];

function RegistrationFlowContent() {
  const navigate = useNavigate();
  const checkoutLayout = useCheckoutLayout();
  const { currentStep, eventInfo, isLoading, error, goToStep, prevStep } =
    useRegistration();

  const handleBack = useCallback(() => {
    if (currentStep === 0) {
      navigate(-1);
    } else {
      prevStep();
    }
  }, [currentStep, navigate, prevStep]);

  useEffect(() => {
    checkoutLayout?.setBackHandler(handleBack);
    return () => checkoutLayout?.setBackHandler(null);
  }, [checkoutLayout, handleBack]);

  if (isLoading || !eventInfo) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <Skeleton className="h-5 w-80" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
        <Skeleton className="h-px w-full" />
        <div className="space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
        <Skeleton className="h-px w-full" />
        <div className="flex justify-end">
          <Skeleton className="h-10 w-44" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const dateRange = formatDateRangeInTimeZone(
    eventInfo.startDate || null,
    eventInfo.endDate || null,
    eventInfo.timezoneIdentifier || null,
  );

  // Use wider container for payment step (two-column layout)
  const containerClass =
    currentStep === 1
      ? "max-w-4xl mx-auto px-4 sm:px-6 py-8"
      : "max-w-3xl mx-auto px-4 sm:px-6 py-8";

  return (
    <div className={containerClass}>
      {/* Event breadcrumb */}
      <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground mb-8">
        <span className="font-medium text-foreground">
          {eventInfo.eventName || "Event"}
        </span>
        {eventInfo.eventSessionName && (
          <>
            <span className="text-muted-foreground/40">/</span>
            <span>{eventInfo.eventSessionName}</span>
          </>
        )}
        {dateRange && dateRange !== "--" && (
          <>
            <span className="text-muted-foreground/40">/</span>
            <span>{dateRange}</span>
          </>
        )}
      </div>

      {/* Step indicator */}
      <StepIndicator
        activeStep={currentStep}
        onStepClick={(step) => goToStep(step)}
        steps={STEPS}
      />

      {/* Active step */}
      <div>
        {currentStep === 0 && <ReviewStep />}
        {currentStep === 1 && <EventCheckoutShellAdapter />}
      </div>
    </div>
  );
}

export default function EventRegistrationPage() {
  const { eventSessionUuid } = useParams<{ eventSessionUuid: string }>();

  if (!eventSessionUuid) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            No event session specified. Please go back and select a session.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <RegistrationProvider eventSessionUuid={eventSessionUuid}>
      <RegistrationFlowContent />
    </RegistrationProvider>
  );
}
